import { lazy, type ComponentType } from 'react';

// EAA-1670: Recover from stale lazy-chunk fetches after a production deploy.
// Every deploy re-hashes chunk filenames. A browser tab still running the
// previous build references the OLD chunk names; when the user navigates to a
// lazy route, import() 404s ("Failed to fetch dynamically imported module")
// because nginx no longer serves the old file, React.lazy throws, and — with no
// error boundary above <Suspense> — the app white-screens.
//
// index.html is served no-cache (see nginx.conf), so a FULL reload always pulls
// fresh chunk references. This wrapper: (1) retries once for a transient blip,
// then (2) forces a single guarded reload so the tab picks up the new build.
// The sessionStorage guard prevents an infinite reload loop if the import fails
// for a real reason (genuine bug, offline) rather than a stale deploy.

const RELOAD_GUARD_PREFIX = 'chunk-reload:';

function isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
        message
    );
}

/**
 * Drop-in replacement for React.lazy that survives stale-chunk failures after a
 * deploy. `chunkId` must be stable and unique per call (used as the reload
 * guard key) — pass the module path.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
    factory: () => Promise<{ default: T }>,
    chunkId: string
) {
    return lazy(async () => {
        const guardKey = `${RELOAD_GUARD_PREFIX}${chunkId}`;
        try {
            const mod = await factory();
            // Success — clear any prior guard so a future stale deploy can reload again.
            window.sessionStorage.removeItem(guardKey);
            return mod;
        } catch (error) {
            if (isChunkLoadError(error)) {
                // Retry once in case it was a transient network hiccup.
                try {
                    const mod = await factory();
                    window.sessionStorage.removeItem(guardKey);
                    return mod;
                } catch (retryError) {
                    if (!window.sessionStorage.getItem(guardKey)) {
                        window.sessionStorage.setItem(guardKey, '1');
                        window.location.reload();
                        // Return a never-resolving promise so Suspense keeps the
                        // fallback shown while the reload takes over the page.
                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                        return new Promise<{ default: T }>(() => {});
                    }
                    throw retryError;
                }
            }
            throw error;
        }
    });
}
